import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { advisorService } from '../services/api';
import { getDisplayName } from '../utils/displayName';
import { applyTheme, getStoredTheme } from '../utils/theme';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout, isImpersonating, stopImpersonation } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [adminUnreadCount, setAdminUnreadCount] = useState(0);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const [darkMode, setDarkMode] = useState(() => getStoredTheme());

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  const categories = useMemo(() => ([
    {
      name: 'Overview',
      items: [{ name: 'Dashboard', href: '/admin/dashboard' }],
    },
    {
      name: 'People',
      items: [
        { name: 'Prospects', href: '/admin/prospects' },
        { name: 'Users', href: '/admin/users' },
        { name: 'Professors', href: '/admin/professors' },
      ],
    },
    {
      name: 'Programs',
      items: [
        { name: 'Programs', href: '/admin/programs' },
        { name: 'Scholarships', href: '/admin/scholarships-management' },
        { name: 'Discount Organizations', href: '/admin/partner-discounts' },
      ],
    },
    {
      name: 'Engagement',
      items: [
        { name: 'Info Sessions', href: '/admin/info-sessions' },
        { name: 'Testimonials', href: '/admin/testimonials' },
        { name: 'Advisor Inbox', href: '/admin/advisor-inbox' },
        { name: 'Analytics', href: '/admin/analytics' },
      ],
    },
    {
      name: 'Partners',
      items: [{ name: 'Partners', href: '/admin/partners' }],
    },
  ]), []);
  useEffect(() => {
    setOpenCategory(null);
    setUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    applyTheme(darkMode);
  }, [darkMode]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!userMenuRef.current) return;
      if (!(event.target instanceof Node)) return;
      if (!userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    const unreadPollMs = process.env.NODE_ENV === 'production' ? 60000 : 5000;
    const loadUnread = async () => {
      if (!user?.is_admin) {
        setAdminUnreadCount(0);
        return;
      }
      try {
        const data = await advisorService.getUnreadCountAdmin();
        setAdminUnreadCount(data.unread_count || 0);
      } catch {
        // Ignore unread count errors to avoid breaking layout.
      }
    };
    loadUnread();
    if (user?.is_admin) {
      interval = setInterval(loadUnread, unreadPollMs);
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadUnread();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      if (interval) clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user?.is_admin]);

  const handleAdvisorInboxClick = () => {
    navigate('/admin/advisor-inbox');
    setUserMenuOpen(false);
  };

  const toggleDarkMode = () => setDarkMode((prev) => !prev);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-emerald-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900 dark:text-slate-100 text-slate-900">
      {/* Header */}
      <header className="relative border-b border-slate-200 bg-white/80 dark:bg-slate-900/80 backdrop-blur">
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setMenuOpen((prev) => !prev)}
                className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-full border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-200 bg-white/80 dark:bg-slate-800/80 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                aria-controls="admin-mobile-menu"
              >
                {menuOpen ? (
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
              <p className="text-xs uppercase tracking-[0.3em] text-emerald-600 dark:text-emerald-300">Admin</p>
            </div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Admin Dashboard</h1>
              <span className="px-2.5 py-1 text-xs rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200 border border-emerald-200/60 dark:border-emerald-800/70">
                Operations
              </span>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-200 mt-0.5">Manage prospects, programs, and analytics</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              onClick={toggleDarkMode}
              aria-pressed={darkMode}
              className="inline-flex items-center px-3 py-1.5 border border-slate-300 dark:border-slate-700 rounded-full text-slate-600 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {darkMode ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                )}
              </svg>
              {darkMode ? 'Light' : 'Dark'}
            </button>
            <Link
              to="/"
              className="inline-flex items-center px-3 py-1.5 border border-slate-300 dark:border-slate-700 rounded-full text-slate-600 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Home
            </Link>
            <div className="relative" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => setUserMenuOpen((prev) => !prev)}
                className="inline-flex items-center px-3 py-1.5 border border-slate-300 dark:border-slate-700 rounded-full text-slate-600 dark:text-slate-200 bg-white/80 dark:bg-slate-800/80 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
              >
                <span>Hello, {getDisplayName(user)}</span>
                {adminUnreadCount > 0 && (
                  <span className="ml-2 min-w-[20px] h-5 px-1.5 rounded-full bg-amber-400 text-amber-900 text-[11px] font-bold flex items-center justify-center">
                    {adminUnreadCount}
                  </span>
                )}
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50 dark:bg-slate-900 dark:border-slate-800">
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/admin/profile');
                      setUserMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-100 dark:hover:bg-slate-800"
                  >
                    Profile
                  </button>
                  {user?.is_admin && (
                    <button
                      type="button"
                      onClick={handleAdvisorInboxClick}
                      className="w-full flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-100 dark:hover:bg-slate-800"
                    >
                      <span>Advisor Inbox</span>
                      {adminUnreadCount > 0 && (
                        <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-amber-400 text-amber-900 text-[11px] font-bold flex items-center justify-center">
                          {adminUnreadCount}
                        </span>
                      )}
                    </button>
                  )}
                  {isImpersonating && (
                    <button
                      type="button"
                      onClick={async () => {
                        await stopImpersonation();
                        navigate('/admin/dashboard');
                        setUserMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-amber-700 hover:bg-amber-50"
                    >
                      Return to Admin
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      logout();
                      setUserMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-100 dark:hover:bg-slate-800"
                  >
                    <ArrowRightOnRectangleIcon className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {user?.is_admin && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
            <div className="lg:hidden mb-3 flex items-center justify-between gap-3">
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Admin</span>
              <button
                type="button"
                onClick={() => setMenuOpen((prev) => !prev)}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200/70 dark:border-slate-700/70 bg-white/70 dark:bg-slate-900/50 px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-200"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                {menuOpen ? 'Close Admin Menu' : 'Open Admin Menu'}
              </button>
            </div>
            <div className="hidden lg:flex flex-wrap items-center gap-2 text-sm rounded-2xl border border-slate-200/70 dark:border-slate-700/70 bg-white/70 dark:bg-slate-900/50 backdrop-blur-md px-3 py-2 shadow-sm">
              {categories.map((category) => {
                const isOpen = openCategory === category.name;
                const hasActiveChild = category.items.some((item) => isActive(item.href));
                return (
                  <div
                    key={category.name}
                    className="relative pb-2"
                    onMouseEnter={() => setOpenCategory(category.name)}
                    onMouseLeave={() => setOpenCategory(null)}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenCategory(isOpen ? null : category.name)}
                      className={`inline-flex items-center px-3.5 py-1.5 border rounded-full transition-colors ${
                        hasActiveChild
                          ? 'border-emerald-300 text-emerald-700 bg-emerald-50/80 dark:border-emerald-700 dark:text-emerald-200 dark:bg-emerald-900/40'
                          : 'border-transparent text-slate-600 dark:text-slate-200 bg-transparent'
                      } hover:bg-slate-100/70 dark:hover:bg-slate-800/70`}
                    >
                      {category.name}
                    </button>
                    {isOpen && (
                      <div className="absolute left-0 top-full mt-0 w-56 bg-white/95 border border-slate-200/80 rounded-xl shadow-xl py-2 z-50 dark:bg-slate-950/95 dark:border-slate-800/80 backdrop-blur-md">
                        {category.items.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`block px-4 py-2 text-sm ${
                              isActive(item.href)
                                ? 'text-emerald-700 font-semibold dark:text-emerald-200'
                                : 'text-slate-700 hover:bg-slate-100/70 dark:text-slate-100 dark:hover:bg-slate-800/70'
                            }`}
                            onClick={() => setOpenCategory(null)}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </header>
      {menuOpen && (
        <div id="admin-mobile-menu" className="lg:hidden bg-white/90 border-b border-gray-200 shadow-sm dark:bg-slate-950/90 dark:border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex flex-col gap-3">
              {categories.map((category) => {
                const isOpen = openCategory === category.name;
                const hasActiveChild = category.items.some((item) => isActive(item.href));
                return (
                  <div key={category.name} className="rounded-xl border border-slate-200/70 dark:border-slate-800/70 bg-white/70 dark:bg-slate-900/50">
                    <button
                      type="button"
                      onClick={() => setOpenCategory(isOpen ? null : category.name)}
                      className={`w-full flex items-center justify-between px-4 py-3 text-sm font-semibold ${
                        hasActiveChild
                          ? 'text-emerald-700 dark:text-emerald-200'
                          : 'text-slate-700 dark:text-slate-200'
                      }`}
                    >
                      {category.name}
                      <span className={`text-xs ${isOpen ? 'text-emerald-600 dark:text-emerald-300' : 'text-slate-400'}`}>
                        {isOpen ? 'Hide' : 'Show'}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="pb-2">
                        {category.items.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => {
                              setMenuOpen(false);
                              setOpenCategory(null);
                            }}
                            className={`block mx-2 my-1 rounded-lg px-3 py-2 text-sm ${
                              isActive(item.href)
                                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                                : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-900'
                            }`}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-slate-900 dark:text-slate-100">
        {children}
      </main>
    </div>
  );
};

export default Layout;
